import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import QuoteModal from '../../components/feature/QuoteModal';
import BookingModal from '../../components/feature/BookingModal';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [isSubmittingNewsletter, setIsSubmittingNewsletter] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedService, setSelectedService] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingNewsletter(true);
    setNewsletterStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('email', newsletterEmail);

      const response = await fetch('https://readdy.ai/api/form/d5kcrcdporqtj2nscal0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setNewsletterStatus('success');
        setNewsletterEmail('');
        setTimeout(() => setNewsletterStatus('idle'), 3000);
      } else {
        setNewsletterStatus('error');
      }
    } catch (error) {
      setNewsletterStatus('error');
    } finally {
      setIsSubmittingNewsletter(false);
    }
  };

  const handleServiceBooking = (serviceName: string) => {
    setSelectedService(serviceName);
    setShowBookingModal(true);
  };

  const services = [
    {
      icon: 'ri-home-4-line',
      title: 'Residential House Cleaning',
      description: 'Complete home cleaning services for a spotless living space',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: 'ri-sofa-line',
      title: 'Sofa Cleaning',
      description: 'Professional deep cleaning for sofas and upholstery',
      color: 'from-gray-900 to-gray-800'
    },
    {
      icon: 'ri-layout-grid-line',
      title: 'Carpet Cleaning',
      description: 'Expert carpet cleaning for fresh, stain-free floors',
      color: 'from-blue-600 to-blue-700'
    },
    {
      icon: 'ri-key-line',
      title: 'End of Lease / Bond Cleaning',
      description: 'Thorough cleaning to ensure you get your bond back',
      color: 'from-gray-800 to-gray-900'
    },
    {
      icon: 'ri-sparkling-2-line',
      title: 'Deep Cleaning',
      description: 'Comprehensive deep clean for every corner of your space',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: 'ri-truck-line',
      title: 'Move-in / Move-out Cleaning',
      description: 'Complete cleaning service for relocations',
      color: 'from-gray-900 to-gray-800'
    },
    {
      icon: 'ri-window-line',
      title: 'Window Cleaning',
      description: 'Crystal clear windows inside and out',
      color: 'from-blue-600 to-blue-700'
    },
    {
      icon: 'ri-contrast-drop-line',
      title: 'Pet Odour Removal',
      description: 'Eliminate pet odours and restore freshness to your home',
      color: 'from-gray-800 to-gray-900'
    },
    {
      icon: 'ri-leaf-line',
      title: 'Eco-Friendly Cleaning',
      description: 'Safe, sustainable cleaning with environmentally friendly products',
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: 'ri-water-flash-line',
      title: 'High Pressure Cleaning',
      description: 'Powerful exterior cleaning for driveways, patios, and more',
      color: 'from-gray-900 to-gray-800'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="w-full px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://static.readdy.ai/image/586daf95ff31b6e4372124409d2be3a5/15b9466de0dc1fa532fc8d86bafa01a8.jpeg" 
                alt="Vanilla Cleaning Logo" 
                className="h-12 w-auto"
              />
            </div>
            
            <div className="flex items-center space-x-8">
              <button onClick={() => scrollToSection('services')} className={`text-sm font-light transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>Services</button>
              <button onClick={() => scrollToSection('about')} className={`text-sm font-light transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>About</button>
              <button onClick={() => scrollToSection('contact')} className={`text-sm font-light transition-colors cursor-pointer ${isScrolled ? 'text-gray-700 hover:text-blue-600' : 'text-white/80 hover:text-white'}`}>Contact</button>
            </div>

            <div className="flex items-center space-x-4">
              <a href="mailto:vanillacleaning1@gmail.com" className={`text-sm font-medium hover:underline cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}>vanillacleaning1@gmail.com</a>
              <button onClick={() => setShowQuoteModal(true)} className="px-6 py-2.5 bg-white text-blue-600 rounded-full text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">
                Request a Quote
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-start overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/abad949786eabe367d793d56a95d7765.jpg"
            alt="Professional cleaning service in action"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/30"></div>
        </div>

        <div className="relative z-10 w-full px-8 max-w-7xl mx-auto">
          <div className="max-w-2xl">
            <h1 className="text-7xl font-bold text-white mb-6 tracking-wide leading-tight" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.3)' }}>
              VANILLA<br />CLEANING
            </h1>
            <p className="text-xl text-white mb-10 leading-relaxed font-light" style={{ textShadow: '0 1px 5px rgba(0,0,0,0.3)' }}>
              Australia's trusted professional cleaning services<br />transforming homes across the nation
            </p>
            <div className="flex items-center space-x-6">
              <button onClick={() => setShowQuoteModal(true)} className="px-8 py-4 bg-blue-600 text-white rounded-full text-base font-medium hover:bg-blue-700 transition-all flex items-center space-x-2 shadow-lg whitespace-nowrap cursor-pointer">
                <span>Get Free Quote</span>
                <i className="ri-arrow-right-line text-xl"></i>
              </button>
              <button onClick={() => scrollToSection('services')} className="text-white text-base font-light hover:underline cursor-pointer whitespace-nowrap">
                View Our Services
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-[#F8FAFB]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-4 font-light">OUR EXPERTISE</p>
            <h2 className="text-6xl font-extrabold text-gray-900 leading-tight">
              Professional Cleaning<br />Services
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <div 
                key={index}
                className={`bg-gradient-to-br ${service.color} rounded-2xl p-8 relative overflow-hidden min-h-[320px] flex flex-col justify-between cursor-pointer hover:shadow-xl transition-all group`}
              >
                <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full -mr-24 -mt-24"></div>
                <div className="relative z-10">
                  <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-6">
                    <i className={`${service.icon} text-3xl text-white`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{service.title}</h3>
                  <p className="text-white/90 text-base leading-relaxed">
                    {service.description}
                  </p>
                </div>
                <div className="relative z-10 mt-6">
                  <button onClick={() => handleServiceBooking(service.title)} className="bg-white/20 hover:bg-white/30 text-white px-6 py-3 rounded-xl font-semibold transition-all whitespace-nowrap group-hover:translate-x-2 duration-300 cursor-pointer">
                    Schedule <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-8 text-center">
          <div className="inline-block px-6 py-2 border-2 border-gray-800 rounded-full mb-12">
            <span className="text-sm uppercase tracking-wider text-gray-800 font-medium">WHY CHOOSE US</span>
          </div>

          <h2 className="text-5xl text-gray-900 leading-relaxed mb-8">
            We bring <span className="inline-flex items-center mx-2"><i className="ri-spray-line text-5xl text-blue-600"></i></span> professional care<br />
            with <span className="inline-flex items-center mx-2"><i className="ri-hand-heart-line text-5xl text-blue-600"></i></span> attention to detail<br />
            in every <span className="inline-flex items-center mx-2"><i className="ri-brush-line text-5xl text-blue-600"></i></span> corner
          </h2>
          <p className="text-xl text-gray-600 mt-8 font-light">
            Proudly serving Australian homes with excellence
          </p>
        </div>
      </section>

      {/* Showcase Section */}
      <section className="py-24 bg-[#FAFBFC]">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid grid-cols-2 gap-8 mb-12">
            <div>
              <h2 className="text-6xl text-gray-900 leading-tight">Transforming Spaces</h2>
            </div>
            <div className="flex items-center">
              <p className="text-gray-600 text-base leading-relaxed">
                Experience the difference of a truly clean home. With years of expertise and a passion for perfection, we transform ordinary spaces into extraordinary sanctuaries. Our dedicated Australian professionals use premium products and proven methods to ensure every corner sparkles with freshness and care.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Gallery Feature */}
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-3xl p-10 relative overflow-hidden min-h-[600px] flex flex-col justify-between cursor-pointer hover:shadow-2xl transition-all group">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
              <div className="relative z-10">
                <div className="inline-flex items-center px-4 py-2 bg-white/20 rounded-full mb-6">
                  <i className="ri-gallery-line text-white mr-2"></i>
                  <span className="text-white text-sm font-medium">Featured Gallery</span>
                </div>
                <h3 className="text-4xl font-bold text-white mb-4">Before & After Transformations</h3>
                <p className="text-white/90 text-base leading-relaxed mb-8">
                  See the remarkable transformations we achieve. Our attention to detail makes all the difference in creating spaces you'll love.
                </p>
              </div>
              <div className="relative z-10">
                <div className="w-full h-64 mb-6">
                  <img 
                    src="https://readdy.ai/api/search-image?query=sparkling%20clean%20modern%20kitchen%20with%20white%20cabinets%20and%20soft%20blue%20accents%2C%20marble%20countertops%2C%20professional%20cleaning%20results%2C%20pristine%20organized%20space%2C%20bright%20natural%20lighting%2C%20stainless%20steel%20appliances%20gleaming%2C%20spotless%20surfaces%2C%20contemporary%20home%20interior%20with%20vanilla%20white%20walls%20and%20subtle%20blue%20details%2C%20fresh%20clean%20atmosphere&width=600&height=400&seq=kitchen-vanilla-clean&orientation=landscape"
                    alt="Clean kitchen"
                    className="w-full h-full object-cover object-top rounded-2xl shadow-xl"
                  />
                </div>
                <button onClick={() => setShowQuoteModal(true)} className="px-8 py-4 bg-white text-blue-600 rounded-full font-semibold hover:bg-gray-50 transition-all whitespace-nowrap cursor-pointer group-hover:scale-105 duration-300">
                  View Full Gallery <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>
            </div>

            {/* Right Column - Service Cards */}
            <div className="grid grid-cols-1 gap-8">
              {/* Standard Clean Card */}
              <div className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all cursor-pointer border-2 border-gray-100 hover:border-blue-500 group">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="inline-flex items-center px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium mb-4">
                      <i className="ri-checkbox-circle-fill mr-1"></i>
                      Verified Service
                    </div>
                    <h4 className="text-2xl font-bold text-gray-900 mb-2">Standard Clean</h4>
                    <p className="text-gray-600 text-sm mb-4">Perfect for regular maintenance and keeping your home fresh and tidy.</p>
                  </div>
                  <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center flex-shrink-0">
                    <i className="ri-home-4-line text-3xl text-blue-600"></i>
                  </div>
                </div>
                <div className="flex items-center justify-between mb-6 pb-6 border-b border-gray-200">
                  <div className="flex items-center text-gray-700">
                    <i className="ri-time-line text-blue-600 mr-2"></i>
                    <span className="text-sm font-medium">2-3 hours</span>
                  </div>
                  <div className="flex items-center">
                    <span className="text-sm text-gray-500 mr-2">From</span>
                    <span className="text-2xl font-bold text-gray-900">$89</span>
                  </div>
                </div>
                <button onClick={() => setShowQuoteModal(true)} className="w-full px-6 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all whitespace-nowrap cursor-pointer group-hover:scale-105 duration-300">
                  Request a Quote <i className="ri-arrow-right-line ml-2"></i>
                </button>
              </div>

              {/* Premium Clean Card */}
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all cursor-pointer relative overflow-hidden group">
                <div className="absolute bottom-0 right-0 w-64 h-64 bg-blue-500/20 rounded-full -mr-32 -mb-32"></div>
                <div className="relative z-10">
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="inline-flex items-center px-3 py-1 bg-blue-500 text-white rounded-full text-xs font-medium mb-4">
                        <i className="ri-vip-crown-line mr-1"></i>
                        Premium Service
                      </div>
                      <h4 className="text-2xl font-bold text-white mb-2">Deep Clean</h4>
                      <p className="text-white/80 text-sm mb-4">Comprehensive cleaning for a spotless home. Ideal for move-ins or seasonal refresh.</p>
                    </div>
                    <div className="w-16 h-16 bg-blue-500/30 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <i className="ri-sparkling-2-line text-3xl text-blue-400"></i>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-6 pb-6 border-b border-white/20">
                    <div className="flex items-center text-white/90">
                      <i className="ri-time-line text-blue-400 mr-2"></i>
                      <span className="text-sm font-medium">4-5 hours</span>
                    </div>
                    <div className="flex items-center">
                      <span className="text-sm text-white/60 mr-2">From</span>
                      <span className="text-2xl font-bold text-white">$149</span>
                    </div>
                  </div>
                  <button onClick={() => setShowQuoteModal(true)} className="w-full px-6 py-4 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-all whitespace-nowrap cursor-pointer group-hover:scale-105 duration-300">
                    Request a Quote <i className="ri-arrow-right-line ml-2"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-32 overflow-hidden mb-8">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=elegant%20minimalist%20clean%20modern%20living%20room%20interior%20with%20pristine%20white%20walls%20and%20soft%20blue%20accent%20chair%2C%20bright%20spacious%20contemporary%20home%20with%20large%20windows%20and%20natural%20sunlight%2C%20spotless%20organized%20residential%20space%2C%20fresh%20vanilla%20white%20atmosphere%2C%20professional%20cleaning%20showcase%2C%20immaculate%20hardwood%20floors%20and%20surfaces%2C%20serene%20peaceful%20environment%20with%20simple%20blue%20decorative%20pillow%2C%20luxury%20home%20design%20with%20clean%20lines%20and%20minimal%20furniture&width=1920&height=800&seq=cta-vanilla-simple-interior-v3&orientation=landscape"
            alt="Professional cleaning team at work"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/25 to-black/35"></div>
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto px-8">
          <h2 className="text-6xl font-extrabold text-white mb-6 tracking-wide">
            READY FOR A CLEANER HOME?
          </h2>
          <p className="text-xl text-white mb-10 leading-relaxed font-light">
            Book your first cleaning service today and experience<br />the difference professional care makes
          </p>
          <button onClick={() => setShowQuoteModal(true)} className="inline-flex items-center px-10 py-5 bg-blue-600 text-white rounded-full text-base font-bold hover:bg-blue-700 transition-all shadow-2xl whitespace-nowrap cursor-pointer">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mr-4">
              <i className="ri-calendar-line text-blue-600 text-xl"></i>
            </div>
            <span className="mr-4">SCHEDULE NOW</span>
            <i className="ri-arrow-right-up-line text-2xl"></i>
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer id="contact" className="bg-[#1E3A5F] mx-4 mb-4 rounded-3xl py-16 px-8 relative overflow-hidden">
        <div className="absolute bottom-0 left-0 right-0 text-[#2A4A6F] text-9xl font-bold opacity-50 text-center pointer-events-none" style={{ lineHeight: '1' }}>
          VANILLA CLEAN
        </div>

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="grid grid-cols-12 gap-12 mb-16">
            {/* Column 1 */}
            <div className="col-span-4">
              <div className="flex items-center space-x-2 mb-4">
                <img 
                  src="https://static.readdy.ai/image/586daf95ff31b6e4372124409d2be3a5/15b9466de0dc1fa532fc8d86bafa01a8.jpeg" 
                  alt="Vanilla Cleaning Logo" 
                  className="h-10 w-auto"
                />
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                Australia's professional cleaning services that transform your space into a sanctuary of pure cleanliness.
              </p>
            </div>

            {/* Column 2 */}
            <div className="col-span-2">
              <h4 className="text-white text-base font-semibold mb-6">Quick Links</h4>
              <ul className="space-y-4">
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">Services</button></li>
                <li><button onClick={() => setShowQuoteModal(true)} className="text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">Pricing</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">About</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">Contact</button></li>
              </ul>
            </div>

            {/* Column 3 */}
            <div className="col-span-2">
              <h4 className="text-white text-base font-semibold mb-6">Connect With Us</h4>
              <ul className="space-y-4">
                <li>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">
                    <i className="ri-instagram-line text-lg"></i>
                    <span>Instagram</span>
                  </a>
                </li>
                <li>
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">
                    <i className="ri-facebook-line text-lg"></i>
                    <span>Facebook</span>
                  </a>
                </li>
                <li>
                  <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="flex items-center space-x-2 text-gray-400 text-sm hover:text-blue-400 transition-colors cursor-pointer">
                    <i className="ri-linkedin-line text-lg"></i>
                    <span>LinkedIn</span>
                  </a>
                </li>
              </ul>
            </div>

            {/* Column 4 */}
            <div className="col-span-4">
              <h4 className="text-white text-base font-semibold mb-4">Stay Updated</h4>
              <p className="text-gray-400 text-sm mb-4 leading-relaxed">
                Subscribe to our newsletter for cleaning tips and special offers
              </p>
              <form id="newsletter-form" data-readdy-form onSubmit={handleNewsletterSubmit} className="space-y-2">
                <div className="flex items-center">
                  <input 
                    type="email"
                    name="email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    placeholder="Your email address"
                    className="flex-1 px-4 py-3 bg-[#2A4A6F] border border-gray-700 rounded-l-full text-white text-sm focus:outline-none focus:border-blue-400"
                    required
                  />
                  <button type="submit" disabled={isSubmittingNewsletter} className="w-12 h-12 bg-blue-600 hover:bg-blue-700 rounded-r-full flex items-center justify-center transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                    <i className="ri-arrow-right-line text-white text-xl"></i>
                  </button>
                </div>
                {newsletterStatus === 'success' && (
                  <p className="text-green-400 text-sm">Thank you for subscribing!</p>
                )}
                {newsletterStatus === 'error' && (
                  <p className="text-red-400 text-sm">Something went wrong. Please try again.</p>
                )}
              </form>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 flex items-center justify-between">
            <p className="text-gray-500 text-sm">© 2025 Vanilla Cleaning Australia. All rights reserved.</p>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-gray-500 text-sm hover:text-blue-400 transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <QuoteModal isOpen={showQuoteModal} onClose={() => setShowQuoteModal(false)} />
      <BookingModal isOpen={showBookingModal} onClose={() => setShowBookingModal(false)} serviceName={selectedService} />
    </div>
  );
}
